import { Form, Formik, FormikErrors } from 'formik'
import { useRouter } from 'next/router'
import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { FaChevronRight } from 'react-icons/fa'
import {
  PiFootprints,
  PiCertificate,
  PiHeartStraight,
  PiPlant
} from 'react-icons/pi'

import { useAuth } from 'src/providers/AuthProvider'
import { FormLayoutProvider } from 'src/providers/FormLayoutProvider'
import { NotificationType, notify } from 'src/providers/NotificationProvider'
import { useUser } from 'src/providers/UserProvider'

import { Button } from 'src/components/atoms/Button'
import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Divider } from 'src/components/atoms/Divider'
import { Heading4 } from 'src/components/atoms/Typography'
import { FormField } from 'src/components/molecules/FormField'

import { useCreateAccountMutation } from 'src/config/generated/graphql'
import { dashboardPath } from 'src/config/paths'
import { AccountType } from 'src/types/account'
import { FormFieldTypes } from 'src/types/form'
import { accountTypeOptions } from 'src/utils/account'
import { trackEvent } from 'src/utils/ga'

import { AccountFormFieldSets } from './AccountFormFieldSets'
import {
  businessSizeOptions,
  departmentOptions,
  personalPrimaryGoalOptions,
  primaryGoalLabelMap,
  transformFormData
} from './helper'
import {
  AccountActionType,
  AccountFormValues,
  ProfileVisibility,
  PrimaryGoalType
} from './types'
import { validationSchema } from './validationSchema'

const DEFAULT_AVATAR = 'assets/img/default-account.png'
const DEFAULT_BANNER = 'assets/img/banners/account-banner-1.jpg'

const FormContainer = ({ children }: PropsWithChildren) => {
  return (
    <div className='rounded-[10px] bg-white p-5 md:p-[60px]'>{children}</div>
  )
}

export const Icon = ({ children }: PropsWithChildren) => {
  return (
    <div
      className='rounded-full bg-mostlyGreen p-3 text-white flex items-center justify-center'
      style={{ width: '60px', height: '60px' }}
    >
      {children}
    </div>
  )
}

export const AccountForm = ({
  onAccountTypeChange
}: {
  onAccountTypeChange?: (type: string) => void
}) => {
  const router = useRouter()
  const { cache } = useAuth()

  const [isBusinessOnboarding, setBusinessOnboarding] = useState(
    cache?.onboard === 'business'
  )

  // `onboard=plant` (e.g. a "Create an account" link from become-a-planter)
  // starts the Plant flow; every other entry point defaults to Fund.
  const isPlanter = cache?.onboard === 'plant'

  // The in-form Fund/Plant choice is hidden — the account action is decided by
  // the entry point (onboard param), not picked here. Set this to
  // `!isBusinessOnboarding && !isPlanter` to bring the choice card back.
  const showAccountActionChoice = false

  const [accountCreated, setAccountCreated] = useState(false)

  const { user, refetchUser } = useUser()
  const [accountType, setAccountType] = useState<string>('')
  const [createAccount] = useCreateAccountMutation()

  useEffect(() => {
    onAccountTypeChange?.(accountType)
  }, [accountType, onAccountTypeChange])

  const initialValues = useMemo<AccountFormValues>(
    () => ({
      AccountType: isBusinessOnboarding ? AccountType.Business : '',
      Avatar: DEFAULT_AVATAR,
      AvatarFile: [],
      Banner: DEFAULT_BANNER,
      BannerFile: [],
      Email: user?.Email || '',
      FirstName: user?.FirstName || '',
      LastName: user?.LastName || '',
      DisplayName: '',
      FocusArea: '',
      Sector: '',
      BusinessType: '',
      SchoolType: '',
      OrgType: '',
      GovType: '',
      Phone: '',
      AccountActionType: isPlanter
        ? AccountActionType.Plant
        : AccountActionType.Donate,
      FunderHidden: ProfileVisibility.Private,
      PlanterHidden: ProfileVisibility.Private,
      FunderName: '',
      PlanterName: '',
      BusinessSize: '',
      PrimaryGoal: '',
      PrimaryGoalNote: '',
      Department: '',
      JobTitle: ''
    }),
    [user, isBusinessOnboarding, isPlanter]
  )

  useEffect(() => {
    async function handleRedirect() {
      if (cache?.returnTo && cache?.returnTo !== router.pathname) {
        await router.push(cache?.returnTo)
      } else {
        await router.push(dashboardPath())
      }
    }

    // Check if we have an account in user context
    if (user?.Account?.Id && accountCreated) {
      handleRedirect()
    }
  }, [cache, router, user?.Account?.Id, accountCreated])

  const handleSubmit = async (
    values: AccountFormValues,
    validateFn: (values?: any) => Promise<FormikErrors<AccountFormValues>>
  ) => {
    const errors = await validateFn(values)
    if (Object.keys(errors).length === 0) {
      const transformedFormData = transformFormData(values)
      await createAccount({
        variables: transformedFormData,
        onCompleted: async response => {
          if (response.account?.Id) {
            const result = await refetchUser()
            const account = result.data?.User?.Account
            if (account?.Funder)
              trackEvent('create_account', {
                account_type: account?.Type,
                business_type: account?.BusinessType,
                business_size: account?.BusinessSize
              })
            notify({
              message: `You've successfully set up your account`,
              type: NotificationType.success
            })
            setAccountCreated(true)
          }
        }
      })
    } else {
      notify({
        message:
          'You have one or more mandatory fields not filled in or checked.',
        type: NotificationType.error
      })
    }
  }

  return (
    <FormLayoutProvider margin='small'>
      <FormContainer>
        <Formik
          initialValues={initialValues}
          onSubmit={() => {}}
          validateOnMount
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values, setTouched, validateForm, resetForm }) => {
            if (accountType !== values.AccountType) {
              setAccountType(values.AccountType)
            }
            return (
              <Form>
                <div className='flex flex-col gap-5 md:gap-10'>
                  {showAccountActionChoice ? (
                    <div className='flex flex-col gap-5'>
                      <div className='flex flex-col gap-[10px]'>
                        <Heading4 className='text-[24px]! leading-[26px]! font-semibold! m-0!'>
                          What brings you to Trees That Count?
                        </Heading4>
                      </div>
                      <FormField
                        id='AccountActionType'
                        type={FormFieldTypes.cardSwitch}
                        labelClassName='sr-only'
                        label='Action type'
                        onChange={() => {
                          setTouched({}, false)
                        }}
                        switchOptions={[
                          {
                            title: 'Fund',
                            description:
                              'I want to fund native tree planting and see my impact.',
                            value: AccountActionType.Donate,
                            imageUrl: '/img/icons/Hand-plant-icon.svg'
                          },
                          {
                            title: 'Plant',
                            description:
                              'I want to request native trees for a planting project.',
                            value: AccountActionType.Plant,
                            imageUrl: '/img/icons/Shovel-icon.svg'
                          }
                        ]}
                      />
                      <Divider className='mt-0!' />
                    </div>
                  ) : null}

                  {!values.AccountActionType ? null : (
                    <>
                      <div className='flex flex-col'>
                        {!isBusinessOnboarding ? (
                          <>
                            <Heading4 className='text-[24px]! leading-[26px]! font-semibold! m-0! mb-5!'>
                              Account details
                            </Heading4>
                            <FormField
                              id='AccountType'
                              label='Account type'
                              options={accountTypeOptions}
                              type={FormFieldTypes.select}
                            />
                          </>
                        ) : null}

                        {values.AccountType === AccountType.Business &&
                        values.AccountActionType ===
                          AccountActionType.Donate ? (
                          <>
                            <div className='flex flex-row justify-between'>
                              <Heading4 className='text-[24px]! leading-[26px]! font-semibold!'>
                                What&apos;s your primary goal?
                              </Heading4>
                              {isBusinessOnboarding ? (
                                <Button
                                  variant='outlined'
                                  size='small'
                                  onClick={() => {
                                    setBusinessOnboarding(false)
                                    cache.onboard = undefined
                                    resetForm({
                                      values: {
                                        ...values,
                                        AccountType: '',
                                        PrimaryGoal: '',
                                        BusinessSize: '',
                                        DisplayName: '',
                                        Department: '',
                                        JobTitle: '',
                                        BusinessType: '',
                                        OrgType: '',
                                        GovType: '',
                                        SchoolType: ''
                                      }
                                    })
                                  }}
                                >
                                  Not a business funder?
                                </Button>
                              ) : null}
                            </div>
                            <div className='my-5 md:my-7'>
                              <FormField
                                id='PrimaryGoal'
                                type={FormFieldTypes.cardSwitch}
                                labelClassName='sr-only'
                                label='Primary goal'
                                onChange={() => {
                                  setTouched({}, false)
                                }}
                                switchOptions={[
                                  {
                                    title:
                                      primaryGoalLabelMap[
                                        PrimaryGoalType.MitigateEmissions
                                      ],
                                    value: PrimaryGoalType.MitigateEmissions,
                                    imageComponent: (
                                      <Icon>
                                        <PiFootprints size='32' />
                                      </Icon>
                                    )
                                  },
                                  {
                                    title:
                                      primaryGoalLabelMap[
                                        PrimaryGoalType.ShowcaseImpact
                                      ],
                                    value: PrimaryGoalType.ShowcaseImpact,
                                    imageComponent: (
                                      <Icon>
                                        <PiCertificate size='32' />
                                      </Icon>
                                    )
                                  },
                                  {
                                    title:
                                      primaryGoalLabelMap[
                                        PrimaryGoalType.EngageStaff
                                      ],
                                    value: PrimaryGoalType.EngageStaff,
                                    imageComponent: (
                                      <Icon>
                                        <PiHeartStraight size='32' />
                                      </Icon>
                                    )
                                  },
                                  {
                                    title:
                                      primaryGoalLabelMap[
                                        PrimaryGoalType.HelpEnvironment
                                      ],
                                    value: PrimaryGoalType.HelpEnvironment,
                                    imageComponent: (
                                      <Icon>
                                        <PiPlant size='32' />
                                      </Icon>
                                    )
                                  }
                                ]}
                              />
                            </div>
                          </>
                        ) : null}

                        <AccountFormFieldSets
                          accountType={values.AccountType as AccountType}
                        />
                        {values.AccountType === AccountType.Business ? (
                          <FormField
                            id='BusinessSize'
                            label='Business size'
                            options={businessSizeOptions}
                            type={FormFieldTypes.select}
                          />
                        ) : null}
                        {values.AccountType ? (
                          <FormField
                            id='DisplayName'
                            label={
                              values.AccountType === AccountType.Business
                                ? 'Business name'
                                : 'Account name'
                            }
                            type={FormFieldTypes.text}
                          />
                        ) : null}
                      </div>
                      {values.AccountType === '' ? null : (
                        <>
                          <Divider className='m-0! -mt-5!' />
                          <div className='flex flex-col'>
                            <Heading4 className='text-[24px]! leading-[26px]! font-semibold! m-0! mb-5!'>
                              {values.AccountType === AccountType.Business
                                ? 'Contact person'
                                : 'Contact information'}
                            </Heading4>
                            <div className='grid gap-4 grid-cols-2'>
                              <div className='col-span-1'>
                                <FormField
                                  id='FirstName'
                                  label='First name'
                                  type={FormFieldTypes.text}
                                />
                              </div>
                              <div className='col-span-1'>
                                <FormField
                                  id='LastName'
                                  label='Last name'
                                  type={FormFieldTypes.text}
                                />
                              </div>
                            </div>
                            {values.AccountType !==
                            AccountType.IndividualFamily ? (
                              <FormField
                                id='Phone'
                                label='Phone number'
                                type={FormFieldTypes.tel}
                              />
                            ) : null}
                            {values.AccountType === AccountType.Business &&
                            values.AccountActionType ===
                              AccountActionType.Donate ? (
                              <>
                                <FormField
                                  id='Department'
                                  label='Department'
                                  options={departmentOptions}
                                  type={FormFieldTypes.select}
                                />
                                <FormField
                                  id='JobTitle'
                                  label='Job title'
                                  type={FormFieldTypes.text}
                                />
                              </>
                            ) : null}
                          </div>
                        </>
                      )}
                    </>
                  )}
                  {values.AccountActionType === AccountActionType.Donate &&
                  values.AccountType !== '' &&
                  values.AccountType !== AccountType.Business ? (
                    <FormField
                      id='PrimaryGoal'
                      label='I want to:'
                      type={FormFieldTypes.select}
                      options={personalPrimaryGoalOptions}
                    />
                  ) : null}
                  {values.PrimaryGoal === PrimaryGoalType.Other &&
                  values.AccountActionType === AccountActionType.Donate &&
                  values.AccountType !== AccountType.Business ? (
                    <FormField
                      id='PrimaryGoalNote'
                      label="Tell us how you'd like to take action"
                      type={FormFieldTypes.textarea}
                    />
                  ) : null}
                  <div className='self-end'>
                    <ButtonRebrand
                      variant='lime'
                      disabled={isSubmitting}
                      type='submit'
                      onClick={async () => {
                        await handleSubmit(values, validateForm)
                      }}
                    >
                      Submit <FaChevronRight className='ml-[10px]' />
                    </ButtonRebrand>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </FormContainer>
    </FormLayoutProvider>
  )
}
